import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import api from '@/lib/api';
import Seo from '@/components/Seo';
import { FUEL_LOCATIONS, locationBySlug } from '@/lib/fuelLocations';
import { MapPin, Trophy, Clock, ArrowLeft, ArrowRight, Sparkles } from 'lucide-react';
const SITE = 'https://tradesmateuk.co.uk';
const FUELS = [
  { key: 'E10', label: 'Unleaded' },
  { key: 'B7', label: 'Diesel' },
  { key: 'E5', label: 'Super Unleaded' },
  { key: 'SDV', label: 'Super Diesel' },
];
const fmtP = (p) => (p == null ? '—' : `${Number(p).toFixed(1)}p`);
const timeAgo = (iso) => {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  return h < 24 ? `${h} hr ago` : `${Math.round(h / 24)} d ago`;
};

export default function FuelLocationPage() {
  const { location } = useParams();
  const loc = locationBySlug(location);
  const [fuel, setFuel] = useState('E10');
  const [radius] = useState(6);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!loc) return;
    let cancelled = false;
    setLoading(true);
    api.get('/fuel/stations', { params: { lat: loc.lat, lng: loc.lng, radius, fuel } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loc, fuel, radius]);

  if (!loc) return <Navigate to="/fuel" replace />;

  const stations = data?.stations || [];
  const cheapest = stations.find((s) => s[fuel] != null);
  const cheapestPrice = cheapest ? fmtP(cheapest[fuel]) : null;
  const nearby = FUEL_LOCATIONS.filter((l) => l.slug !== loc.slug).slice(0, 12);

  const title = `Cheapest Fuel in ${loc.name} — Live Petrol & Diesel Prices`;
  const description = `Find the cheapest petrol and diesel in ${loc.name}, ${loc.region}. Live UK fuel prices from major retailers, updated throughout the day — compare forecourts near you for free.`;

  const faq = [
    { q: `Where is the cheapest petrol in ${loc.name}?`,
      a: `The cheapest unleaded and diesel in ${loc.name} changes daily. This page shows live prices from major retailers within a few miles of the city centre, with the cheapest forecourt highlighted at the top.` },
    { q: `How often do ${loc.name} fuel prices update?`,
      a: `Prices are pulled from the UK government's open fuel-price scheme and refreshed throughout the day, so what you see here closely tracks the pumps in ${loc.region}.` },
    { q: `Which fuel stations in ${loc.name} are included?`,
      a: `We show participating major retailers such as Asda, Tesco, Morrisons, Esso, BP and Motor Fuel Group. Small independents that don't publish price data won't appear.` },
    { q: `Is the ${loc.name} fuel price checker free?`,
      a: `Yes — comparing fuel prices in ${loc.name} is completely free and needs no sign-up. TradesMate is built for UK tradesmen but anyone can use the fuel tracker.` },
  ];

  const jsonLd = [
    { '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Fuel Prices', item: `${SITE}/fuel` },
        { '@type': 'ListItem', position: 2, name: loc.name, item: `${SITE}/fuel/${loc.slug}` },
      ] },
    { '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: faq.map((f) => ({ '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a } })) },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="fuel-location-page">
      <Seo title={title} description={description} path={`/fuel/${loc.slug}`}
        kicker={`Cheapest fuel in ${loc.name}`} jsonLd={jsonLd} />

      <nav className="text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2 flex-wrap font-semibold">
        <Link to="/fuel" className="hover:text-brand" data-testid="breadcrumb-fuel">Fuel Prices</Link>
        <span>/</span>
        <span className="text-slate-700">{loc.name}</span>
      </nav>

      <header className="mb-6">
        <p className="text-xs uppercase tracking-widest text-brand font-semibold">Live · {loc.region} · free</p>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl uppercase tracking-tight mt-1">Cheapest fuel in {loc.name}</h1>
        <p className="text-slate-600 mt-3 leading-relaxed">
          Compare live petrol and diesel prices across {loc.name} and {loc.region}. {loc.blurb} Prices below refresh
          throughout the day from the UK government's open fuel-price scheme{cheapestPrice ? `, and the cheapest ${FUELS.find((f) => f.key === fuel).label.toLowerCase()} we can see right now is ${cheapestPrice} a litre` : ''}.
        </p>
      </header>

      {/* Fuel type toggle */}
      <div className="flex flex-wrap gap-1 mb-4" data-testid="fuel-location-toggle">
        {FUELS.map((ft) => (
          <button key={ft.key} onClick={() => setFuel(ft.key)} data-testid={`fuel-type-${ft.key}`}
            className={`px-3 py-1.5 text-xs font-mono uppercase border ${fuel === ft.key ? 'bg-brand text-white border-brand' : 'bg-white text-slate-600 border-slate-300'}`}>
            {ft.label}
          </button>
        ))}
      </div>

      {loading && <p className="font-mono text-slate-400" data-testid="fuel-location-loading">Loading live {loc.name} prices…</p>}

      {!loading && (
        <div data-testid="fuel-location-results">
          {stations.length === 0 ? (
            <div className="tm-card p-8 text-center text-slate-500">
              We couldn't find live prices near {loc.name} right now — try the{' '}
              <Link to="/fuel" className="text-brand underline">postcode search</Link> for a wider area.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3 text-xs font-mono text-slate-500">
                <span>{data.total_in_radius} stations within {radius} mi of {loc.name}</span>
                {data.updated_at && <span className="flex items-center gap-1"><Clock size={12} /> Updated {timeAgo(data.updated_at)}</span>}
              </div>
              <div className="space-y-2">
                {stations.slice(0, 15).map((s, i) => {
                  const best = cheapest && s.site_id === cheapest.site_id;
                  return (
                    <div key={s.site_id} data-testid={`fuel-station-${i}`}
                      className={`tm-card p-4 flex items-center gap-4 ${best ? 'ring-2 ring-emerald-500' : ''}`}>
                      <div className="text-center shrink-0 w-24">
                        <div className={`font-display font-semibold text-2xl ${best ? 'text-emerald-600' : 'text-slate-900'}`}>{fmtP(s[fuel])}</div>
                        {best && <span className="inline-flex items-center gap-1 text-[10px] uppercase text-emerald-600 font-semibold"><Trophy size={11} /> Cheapest</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold">{s.brand}</p>
                        <p className="text-sm text-slate-500 truncate">{s.address}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-sm font-bold text-slate-700">{s.distance} mi</div>
                        <a href={`https://www.google.com/maps/dir/?api=1&destination=${s.lat},${s.lng}`} target="_blank" rel="noreferrer"
                          className="text-[11px] font-mono text-brand hover:underline">Directions →</a>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4">
                <Link to="/fuel" className="tm-btn tm-btn-ghost inline-flex" data-testid="fuel-location-search-own">
                  <MapPin size={16} /> Search your own postcode
                </Link>
              </div>
              <p className="text-[11px] text-slate-400 mt-4 leading-snug">
                Prices from participating major retailers via the UK government's open fuel-price scheme. Small independents that don't publish data won't appear. Always check at the pump.
              </p>
            </>
          )}
        </div>
      )}

      {/* SEO content */}
      <section className="mt-12 prose-none">
        <h2 className="font-display font-semibold uppercase tracking-tight text-2xl mb-3">How to find the cheapest fuel in {loc.name}</h2>
        <p className="text-slate-700 leading-relaxed mb-2">
          Fuel is one of the biggest running costs for any {loc.name} driver — and for tradesmen doing 20,000+ miles a
          year it's a serious chunk of the wage bill. Prices between forecourts in {loc.region} can differ by 10p a litre
          or more, so a quick compare before you fill can save hundreds of pounds a year.
        </p>
        <ul className="list-disc pl-6 space-y-1 text-slate-700 mt-2">
          <li>Supermarket forecourts (Asda, Tesco, Morrisons, Sainsbury's) are usually the cheapest in {loc.name}.</li>
          <li>Fill up before joining the motorway — service-station fuel is almost always dearer.</li>
          <li>Prices move daily, so check live figures rather than relying on last week's price.</li>
          <li>Diesel and unleaded don't always track together — switch the toggle above to compare your fuel.</li>
        </ul>
      </section>

      <section className="mt-10">
        <h2 className="font-display font-bold text-xl uppercase mb-4">{loc.name} fuel prices — FAQs</h2>
        <div className="space-y-4">
          {faq.map((f, i) => (
            <div key={i} className="tm-card p-4" data-testid={`fuel-location-faq-${i}`}>
              <h3 className="font-bold text-base">{f.q}</h3>
              <p className="text-slate-600 text-sm mt-1">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Nearby cities */}
      <section className="mt-10">
        <h2 className="font-display font-bold text-xl uppercase mb-4">Fuel prices in other UK cities</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {nearby.map((l) => (
            <Link key={l.slug} to={`/fuel/${l.slug}`} data-testid={`fuel-link-${l.slug}`}
              className="tm-card p-3 hover:shadow-lg transition-shadow flex items-center justify-between">
              <span className="font-medium text-sm">{l.name}</span>
              <ArrowRight size={14} className="text-brand" />
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <div className="mt-10 tm-card p-6 border border-slate-200 shadow-md">
        <div className="flex items-start gap-3">
          <Sparkles className="text-brand shrink-0 mt-1" size={24} />
          <div>
            <h2 className="font-display font-bold text-lg uppercase tracking-tight">Run a trade in {loc.name}?</h2>
            <p className="text-slate-600 text-sm mt-1">
              TradesMate gives UK tradesmen 95+ material calculators, an AI estimator, branded quotes and live job
              profit tracking — plus this free fuel tracker. Free calculators forever.
            </p>
            <div className="flex flex-wrap gap-2 mt-3">
              <Link to="/register" data-testid="fuel-location-signup" className="tm-btn inline-flex">Start free</Link>
              <Link to="/fuel" className="tm-btn tm-btn-dark inline-flex">All fuel prices</Link>
            </div>
          </div>
        </div>
      </div>

      <Link to="/fuel" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-600 hover:text-brand mt-8 font-semibold">
        <ArrowLeft size={14} /> All fuel prices
      </Link>
    </div>
  );
}
